import type { FormField } from '@lucid/lego';
import { formatCurrency } from '../../../../../../../utils/tools';

export type SelectOption = {
  value: string;
  label: string;
};

export type BuyerOrderData = {
  customerDetails?: {
    customerFullName?: string | null;
    customerTitle?: string | null;
    customerEmail?: string | null;
  } | null;
  preferredPaymentMethod?: string | null;
  totalAmountDue?: number | null;
};

type GenerateDocumentsFormFieldsOptions = {
  onCancel?: () => void;
  openDueBillStatusOptions?: SelectOption[];
  serviceCenterLocationOptions?: SelectOption[];
  locale?: string;
};

/**
 * Builds form field definitions for the Generate Documents modal.
 *
 * @param options - Runtime options (callbacks, dropdown data)
 * @param labels - i18n labels map
 * @param orderData - Order details data for buyer/payment display values
 * @returns FormField[] configuration for the Form component
 */
export const getGenerateDocumentsFormFields = (
  options?: GenerateDocumentsFormFieldsOptions,
  labels?: Record<string, string>,
  orderData?: BuyerOrderData
): FormField[] => [
  {
    id: 'buyer-info-group',
    fieldType: 'group',
    title: labels?.['buyer.informationTitle'] ?? 'Buyer Information',
    variant: 'default',
    children: [
      {
        id: 'buyerName',
        fieldType: 'content',
        label: labels?.['buyer.name.label'] ?? 'NAME',
        content: orderData?.customerDetails?.customerFullName || '-',
        width: 'third',
      },
      {
        id: 'buyerTitle',
        fieldType: 'content',
        label: labels?.['buyer.title.label'] ?? 'TITLE',
        content: orderData?.customerDetails?.customerTitle || '-',
        width: 'third',
      },
      {
        id: 'buyerEmail',
        fieldType: 'content',
        label: labels?.['buyer.email.label'] ?? 'EMAIL',
        content: orderData?.customerDetails?.customerEmail || '-',
        width: 'third',
      },
    ],
  },
  {
    id: 'payment-details-group',
    fieldType: 'group',
    title: labels?.['payment.detailsTitle'] ?? 'Payment Details',
    variant: 'default',
    children: [
      {
        id: 'purchasePlanStatus',
        fieldType: 'content',
        label: labels?.['payment.purchasePlanStatus.label'] ?? 'PURCHASE PLAN STATUS',
        content:
          labels?.['payment.purchasePlanStatus.value'] ??
          labels?.['payment.paidAndSigned'] ??
          'Paid and Signed',
        width: 'third',
      },
      {
        id: 'paymentType',
        fieldType: 'content',
        label: labels?.['payment.type.label'] ?? 'PAYMENT TYPE',
        content: orderData?.preferredPaymentMethod || '-',
        width: 'third',
      },
      {
        id: 'remainingAmount',
        fieldType: 'content',
        label: labels?.['payment.remainingAmount.label'] ?? 'REMAINING AMOUNT AT HANDOFF',
        content:
          orderData?.totalAmountDue != null
            ? formatCurrency(orderData.totalAmountDue, options?.locale)
            : '-',
        width: 'third',
      },
    ],
  },
  {
    id: 'notes-group',
    fieldType: 'group',
    title: labels?.['notes.title'] ?? 'Notes',
    variant: 'default',
    children: [
      {
        id: 'deliveryInstructions',
        fieldType: 'textarea',
        name: 'deliveryInstructions',
        apiKey: 'deliveryInstructions',
        label: labels?.['deliveryInstructions.label'] ?? 'DELIVERY INSTRUCTIONS',
        placeholder: labels?.['deliveryInstructions.placeholder'] ?? 'Enter Delivery Instructions',
        rows: 4,
        width: 'full',
      },
      {
        id: 'generalNotes',
        fieldType: 'textarea',
        name: 'generalNotes',
        apiKey: 'generalNotes',
        label: labels?.['generalNotes.label'] ?? 'GENERAL NOTES',
        placeholder: labels?.['generalNotes.placeholder'] ?? 'Enter Notes',
        rows: 4,
        width: 'full',
      },
    ],
  },
  {
    id: 'due-bills-group',
    fieldType: 'group',
    title: labels?.['openDueBills.title'] ?? 'Open Due Bills',
    variant: 'default',
    children: [
      {
        id: 'openDueBillStatus',
        fieldType: 'dropdown',
        name: 'openDueBillStatus',
        apiKey: 'openDueBillStatus',
        label: labels?.['openDueBills.statusLabel'] ?? 'STATUS*',
        placeholder: labels?.['openDueBills.statusPlaceholder'] ?? 'Select Status',
        options: options?.openDueBillStatusOptions ?? [],
        required: true,
        width: 'half',
        validationMessages: {
          required: labels?.['openDueBills.statusRequired'] ?? 'Status is required',
        },
      },
      {
        id: 'serviceCenterLocation',
        fieldType: 'dropdown',
        name: 'serviceCenterLocation',
        apiKey: 'serviceCenterLocation',
        label: labels?.['serviceCenter.locationLabel'] ?? 'SERVICE CENTER LOCATION',
        placeholder: labels?.['serviceCenter.locationPlaceholder'] ?? 'Select Location',
        options: options?.serviceCenterLocationOptions ?? [],
        width: 'half',
        disabledDependency: { name: 'openDueBillStatus', value: 'Nothing Owed' },
      },
    ],
  },
  {
    id: 'actions',
    fieldType: 'buttonGroup',
    align: 'center',
    buttons: [
      {
        id: 'cancel',
        label: labels?.['common.cancelButton'] ?? 'Cancel',
        type: 'secondary',
        color: 'neutral',
        onClick: () => options?.onCancel?.(),
        isFluid: true,
      },
      {
        id: 'submit',
        label: labels?.['generateDocuments.button'] ?? 'Generate Documents',
        type: 'primary',
        color: 'neutral',
        isFluid: true,
      },
    ],
  },
];
